import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  Compass,
  Users,
  Waypoints,
  X,
} from "lucide-react";
import { Logo } from "./Logo";
import "../screens/LandingPage.css";

const LANDING_PATH = "/welcome";

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/** Logged-out marketing nav only — no signed-in app destinations (Gallery/Perks). */
const MOBILE_LINKS = [
  { label: "Discover", sectionId: "live-drops", icon: Compass },
  { label: "For Creators", sectionId: "creators", icon: Users },
  { label: "How It Works", sectionId: "how", icon: Waypoints },
];

/**
 * Public/marketing top nav with desktop links + mobile floating popup overlay.
 * Used on Landing and standalone waitlist/application flows.
 */
export default function MarketingNav() {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === LANDING_PATH || location.pathname === "/";

  const closeNav = () => setNavOpen(false);

  useEffect(() => {
    if (!navOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") closeNav();
    };
    const onResize = () => {
      if (window.matchMedia("(min-width: 1000px)").matches) closeNav();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [navOpen]);

  const goToLandingSection = (id) => {
    closeNav();
    if (onLanding) {
      window.setTimeout(() => scrollToId(id), 10);
      return;
    }
    navigate(`${LANDING_PATH}#${id}`);
  };

  return (
    <>
      <header className="lp-nav">
        <div className="lp-nav-inner">
          <Link to={LANDING_PATH} className="lp-nav-brand" aria-label="100ViewsOnly home">
            <Logo />
          </Link>

          <nav className="lp-nav-links">
            <a
              href={`${LANDING_PATH}#live-drops`}
              onClick={(e) => {
                e.preventDefault();
                goToLandingSection("live-drops");
              }}
            >
              Discover
            </a>
            <a
              href={`${LANDING_PATH}#creators`}
              onClick={(e) => {
                e.preventDefault();
                goToLandingSection("creators");
              }}
            >
              For Creators
            </a>
            <a
              href={`${LANDING_PATH}#how`}
              onClick={(e) => {
                e.preventDefault();
                goToLandingSection("how");
              }}
            >
              How it works
            </a>
          </nav>

          <div className="lp-nav-actions">
            <Link to="/welcome?auth=signin" className="lp-btn-ghost">
              Sign in
            </Link>
            <Link to="/waitlist/fan" className="lp-btn-dark">
              Join Free
            </Link>
          </div>

          <button
            type="button"
            className="lp-nav-toggle"
            aria-label={navOpen ? "Close menu" : "Open menu"}
            aria-expanded={navOpen}
            aria-controls="lp-nav-popover"
            onClick={() => setNavOpen((o) => !o)}
          >
            <span className="lp-nav-toggle-bars" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
      </header>

      {navOpen && (
        <div className="lp-nav-overlay" role="presentation">
          <button
            type="button"
            className="lp-nav-scrim"
            aria-label="Close menu"
            onClick={closeNav}
          />
          <div
            id="lp-nav-popover"
            className="lp-nav-popover"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <span className="lp-nav-popover-caret" aria-hidden="true" />

            <div className="lp-nav-popover-header">
              <Link
                to={LANDING_PATH}
                className="lp-nav-popover-brand"
                onClick={closeNav}
                aria-label="100ViewsOnly home"
              >
                <Logo size="sm" />
              </Link>
              <button
                type="button"
                className="lp-nav-popover-close"
                aria-label="Close menu"
                onClick={closeNav}
              >
                <X size={18} strokeWidth={2.2} />
              </button>
            </div>

            <nav className="lp-nav-popover-links">
              {MOBILE_LINKS.map((item, i) => {
                const Icon = item.icon;
                const style = { "--lp-nav-stagger": `${40 + i * 50}ms` };
                return (
                  <a
                    key={item.label}
                    href={`${LANDING_PATH}#${item.sectionId}`}
                    className="lp-nav-popover-link"
                    style={style}
                    onClick={(e) => {
                      e.preventDefault();
                      goToLandingSection(item.sectionId);
                    }}
                  >
                    <span className="lp-nav-popover-icon" aria-hidden="true">
                      <Icon size={15} strokeWidth={2.1} />
                    </span>
                    <span className="lp-nav-popover-label">{item.label}</span>
                    <ChevronRight
                      className="lp-nav-popover-chevron"
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </a>
                );
              })}
            </nav>

            <div className="lp-nav-popover-footer">
              <Link
                to="/welcome?auth=signin"
                className="lp-btn-ghost lp-nav-popover-signin"
                onClick={closeNav}
              >
                Sign In
              </Link>
              <Link
                to="/welcome?auth=signup"
                className="lp-btn-gold lp-nav-popover-join"
                onClick={closeNav}
              >
                Join Free
              </Link>
              <p className="lp-nav-popover-note">
                🍁 Canadian creators · Fans worldwide welcome
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
